/**
 * Notifications / Activity Feed Screen
 *
 * Shows real-time notifications and activity updates.
 * Works with both Supabase backend and demo mode with mock data.
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  RefreshControl,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Stack, useRouter } from 'expo-router';
import Animated, { FadeInDown, FadeInUp, FadeIn, SlideInRight } from 'react-native-reanimated';
import {
  Bell,
  MessageCircle,
  FolderOpen,
  CheckCircle,
  Eye,
  TrendingUp,
  ChevronRight,
  ChevronLeft,
  Check,
  Trash2,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useProfileStore } from '@/lib/state/profile-store';
import { useRealtimeNotifications, RealtimeNotification, useAuth } from '@/lib/supabase';
import { useAlert } from '@/components/AlertDialog';
import { mode } from '@/lib/config/app-mode';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import AnimatedPressable from '@/components/AnimatedPressable';
import { APP_LOCALE } from '@/lib/format';

// Mock notifications for mock mode
const mockNotifications: RealtimeNotification[] = [
  {
    id: '1',
    type: 'message',
    title: 'New message from Sarah Chen',
    body: 'I have a few questions about your Series A projections...',
    createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 min ago
    read: false,
    data: { conversationId: 'conv-1' },
  },
  {
    id: '2',
    type: 'data_room_request',
    title: 'Data room access requested',
    body: 'Michael Roberts from Sequoia Capital has requested access to your data room.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
    read: false,
    data: { investorId: 'inv-2', investorName: 'Michael Roberts' },
  },
  {
    id: '3',
    type: 'data_room_approved',
    title: 'Data room access granted',
    body: 'TechVenture AI has approved your data room access request.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(), // 5 hours ago
    read: true,
    data: { startupId: 'startup-1', startupName: 'TechVenture AI' },
  },
  {
    id: '4',
    type: 'profile_view',
    title: 'Profile viewed',
    body: 'Emily Wong viewed your startup profile.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
    read: true,
    data: { investorId: 'inv-3', investorName: 'Emily Wong' },
  },
  {
    id: '5',
    type: 'score_update',
    title: 'Score updated',
    body: 'Your Inverio Score has been updated to 78/100 based on new data.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(), // 2 days ago
    read: true,
    data: { score: 78 },
  },
  {
    id: '6',
    type: 'message',
    title: 'New message from GreenEnergy Solutions',
    body: 'Thank you for your interest in our company...',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(), // 3 days ago
    read: true,
    data: { conversationId: 'conv-4' },
  },
];

// Icon mapping for notification types
const getNotificationIcons = (colors: ReturnType<typeof useThemeColors>) => ({
  message: { icon: MessageCircle, color: colors.primary[600], bg: colors.primary[50] },
  data_room_request: { icon: FolderOpen, color: colors.traffic.amber, bg: colors.traffic.amberLight },
  data_room_approved: { icon: CheckCircle, color: colors.traffic.green, bg: colors.traffic.greenLight },
  profile_view: { icon: Eye, color: colors.info, bg: colors.primary[50] },
  score_update: { icon: TrendingUp, color: colors.success, bg: colors.traffic.greenLight },
  feedback_update: { icon: MessageCircle, color: colors.traffic.amber, bg: colors.traffic.amberLight },
  waitlist_invited: { icon: CheckCircle, color: colors.primary[600], bg: colors.primary[50] },
  system: { icon: Bell, color: colors.text.secondary, bg: colors.surface.secondary },
});

// Time formatting helper
function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(APP_LOCALE);
}

// Notification Item Component
function NotificationItem({
  notification,
  onPress,
  onMarkRead,
  onDelete,
  index,
}: {
  notification: RealtimeNotification;
  onPress: () => void;
  onMarkRead: () => void;
  onDelete: () => void;
  index: number;
}) {
  const colors = useThemeColors();
  const notificationIcons = getNotificationIcons(colors);
  const iconConfig = notificationIcons[notification.type] || notificationIcons.message;
  const Icon = iconConfig.icon;

  return (
    <Animated.View entering={FadeIn.delay(100 + index * 50).duration(300)}>
      <AnimatedPressable
        onPress={onPress}
        className="flex-row items-start gap-3 p-4 rounded-xl"
        style={{
          backgroundColor: notification.read ? colors.surface.primary : colors.primary[50],
          borderWidth: notification.read ? 0 : 1,
          borderColor: notification.read ? 'transparent' : colors.primary[100],
        }}
      >
        {/* Icon */}
        <View
          className="w-10 h-10 rounded-full items-center justify-center"
          style={{ backgroundColor: iconConfig.bg }}
        >
          <Icon size={20} color={iconConfig.color} />
        </View>

        {/* Content */}
        <View className="flex-1">
          <View className="flex-row items-center justify-between mb-1">
            <Text
              style={{ color: colors.text.primary }}
              className={`text-sm flex-1 ${notification.read ? 'font-medium' : 'font-semibold'}`}
              numberOfLines={1}
            >
              {notification.title}
            </Text>
            <Text style={{ color: colors.text.tertiary }} className="text-xs ml-2">
              {formatTimeAgo(notification.createdAt)}
            </Text>
          </View>
          <Text
            style={{ color: colors.text.secondary }}
            className="text-sm"
            numberOfLines={2}
          >
            {notification.body}
          </Text>
        </View>

        {/* Actions. `shrink-0` so the row's text truncates instead of squeezing
            these below the 44pt touch target. */}
        <View className="flex-row items-center gap-1 shrink-0">
          {!notification.read ? (
            <Pressable
              onPress={(e) => {
                e.stopPropagation();
                onMarkRead();
              }}
              accessibilityRole="button"
              accessibilityLabel="Mark as read"
              className="w-8 h-8 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <Check size={16} color={colors.text.tertiary} />
            </Pressable>
          ) : (
            <ChevronRight size={18} color={colors.text.tertiary} />
          )}

          {/* Delete. There was no way to remove a notification at all — only to
              mark it read — so the list only ever grew. The Trash2 icon was
              already imported by this file and never rendered. */}
          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            accessibilityRole="button"
            accessibilityLabel={`Delete notification: ${notification.title}`}
            className="w-8 h-8 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <Trash2 size={15} color={colors.text.tertiary} />
          </Pressable>
        </View>
      </AnimatedPressable>
    </Animated.View>
  );
}

export default function NotificationsScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const [refreshing, setRefreshing] = useState(false);
  const userRole = useProfileStore((s) => s.userRole);
  // THE AUTH USER ID — not the startup/investor profile id.
  //
  // This read `userRole === 'startup' ? startupProfile?.id : investorProfile?.id`,
  // but `notifications.user_id` references `profiles(id)`, which is the auth
  // user id. The filter therefore matched no rows and this screen was empty
  // for every real user of either persona, while the dashboard bell — which
  // passes `authUser?.id` correctly — showed a red dot from the same table.
  //
  // Because the list was empty there was also nothing to mark read, so the
  // dot and the app-icon badge never cleared. One wrong id space, three
  // symptoms.
  const { user: authUser } = useAuth();
  const userId = authUser?.id;

  // Dynamic breadcrumbs based on user role
  const homHref = userRole === 'startup'
    ? '/(startup)/startup-dashboard'
    : userRole === 'admin'
    ? '/(admin)/admin-dashboard'
    : '/(investor)/investor-dashboard';

  const breadcrumbs: Breadcrumb[] = [
    { label: 'Home', href: homHref },
    { label: 'Notifications' },
  ];

  // Use real-time notifications if Supabase is configured
  const {
    notifications: realtimeNotifications,
    unreadCount: realtimeUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    refresh,
  } = useRealtimeNotifications(userId);
  const { confirmDestructive } = useAlert();

  // Use mock data in mock mode
  const [mockNotificationsState, setMockNotificationsState] = useState(mockNotifications);

  const notifications = mode.isMock ? mockNotificationsState : realtimeNotifications;
  const unreadCount = mode.isMock
    ? mockNotificationsState.filter((n) => !n.read).length
    : realtimeUnreadCount;

  const handleRefresh = async () => {
    setRefreshing(true);
    // A REAL refetch. This used to await a 1s timer and call it a refresh —
    // every user got a spinner and the same stale list. Mock mode has nothing
    // to refetch, so it simply stops spinning.
    try {
      if (!mode.isMock) await refresh();
    } finally {
      setRefreshing(false);
    }
  };

  const handleMarkRead = (notificationId: string) => {
    if (mode.isMock) {
      setMockNotificationsState((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
    } else {
      markAsRead(notificationId);
    }
  };

  const handleDelete = (notificationId: string) => {
    if (mode.isMock) {
      setMockNotificationsState((prev) => prev.filter((n) => n.id !== notificationId));
    } else {
      deleteNotification(notificationId);
    }
  };

  /**
   * Clear everything. Confirmed first — it is the one action here that cannot
   * be undone, and it sits next to "Mark all read", which can.
   */
  const handleClearAll = async () => {
    if (
      !(await confirmDestructive(
        'Clear all notifications',
        'This removes every notification from your list. It cannot be undone.',
        'Clear all',
      ))
    ) {
      return;
    }
    if (mode.isMock) {
      setMockNotificationsState([]);
    } else {
      clearAllNotifications();
    }
  };

  const handleMarkAllRead = () => {
    if (mode.isMock) {
      setMockNotificationsState((prev) => prev.map((n) => ({ ...n, read: true })));
    } else {
      markAllAsRead();
    }
  };

  const handleNotificationPress = (notification: RealtimeNotification) => {
    // Mark as read first
    if (!notification.read) {
      handleMarkRead(notification.id);
    }

    // A notification that names a startup goes to that startup, whatever its
    // `type`. Deck-analysis notifications are `type: 'system'`, which fell into
    // the `default:` branch below and did NOTHING for a non-admin — so "View
    // the results now" was not a link to anything. Routing on the payload
    // rather than the type also means any future startup-scoped notification
    // lands correctly without another case here.
    const startupId = notification.data?.startupId;
    if (typeof startupId === 'string' && startupId.length > 0) {
      router.push(`/startup-detail?id=${startupId}`);
      return;
    }

    // Navigate based on notification type
    switch (notification.type) {
      case 'message':
        if (userRole === 'startup') {
          router.push('/(startup)/startup-messages');
        } else if (userRole === 'investor') {
          router.push('/(investor)/investor-messages');
        } else {
          router.push('/(admin)/admin-dashboard');
        }
        break;
      case 'data_room_request':
        if (userRole !== 'admin') {
          router.push('/(startup)/dataroom');
        } else {
          router.push('/(admin)/admin-dashboard');
        }
        break;
      case 'data_room_approved':
        if (userRole !== 'admin') {
          router.push('/(investor)/duediligence');
        } else {
          router.push('/(admin)/admin-dashboard');
        }
        break;
      case 'profile_view':
        if (userRole === 'startup') {
          router.push('/(startup)/company');
        } else if (userRole === 'admin') {
          router.push('/(admin)/admin-dashboard');
        }
        break;
      case 'score_update':
        if (userRole !== 'admin') {
          router.push('/(startup)/score');
        } else {
          router.push('/(admin)/admin-dashboard');
        }
        break;
      default:
        if (userRole === 'admin') {
          router.push('/(admin)/admin-dashboard');
        }
        break;
    }
  };

  // Group notifications by date
  const groupedNotifications = notifications.reduce<{
    today: RealtimeNotification[];
    yesterday: RealtimeNotification[];
    older: RealtimeNotification[];
  }>(
    (acc, notification) => {
      const date = new Date(notification.createdAt);
      const now = new Date();
      const diffDays = Math.floor(
        (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (diffDays === 0) {
        acc.today.push(notification);
      } else if (diffDays === 1) {
        acc.yesterday.push(notification);
      } else {
        acc.older.push(notification);
      }

      return acc;
    },
    { today: [], yesterday: [], older: [] }
  );

  const content = (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          {/* Header - shows mark all read on web, full header on mobile */}
          <Animated.View
            entering={FadeInDown.duration(400)}
            className="px-5 py-4 flex-row items-center justify-between"
            style={{
              backgroundColor: colors.surface.primary,
              borderBottomWidth: 1,
              borderBottomColor: colors.border.light,
            }}
          >
            {!isWeb && (
              <View className="flex-1">
                <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
                  Notifications
                </Text>
                {unreadCount > 0 && (
                  <Text style={{ color: colors.text.secondary }} className="text-sm">
                    {unreadCount} unread
                  </Text>
                )}
              </View>
            )}
            {isWeb && unreadCount > 0 && (
              <Text style={{ color: colors.text.secondary }} className="text-sm">
                {unreadCount} unread
              </Text>
            )}
            {isWeb && <View className="flex-1" />}
            <View className="flex-row items-center gap-2 shrink-0">
              {unreadCount > 0 && (
                <AnimatedPressable
                  onPress={handleMarkAllRead}
                  accessibilityRole="button"
                  accessibilityLabel="Mark all notifications as read"
                  className="px-3 py-2 rounded-lg"
                  style={{ backgroundColor: colors.primary[50] }}
                >
                  <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                    Mark all read
                  </Text>
                </AnimatedPressable>
              )}
              {/* Clear all. Destructive and confirmed, unlike its neighbour —
                  which is why it is styled as a quiet action rather than a
                  second primary sitting next to "Mark all read". */}
              {notifications.length > 0 && (
                <AnimatedPressable
                  onPress={handleClearAll}
                  accessibilityRole="button"
                  accessibilityLabel="Clear all notifications"
                  className="px-3 py-2 rounded-lg"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <Text style={{ color: colors.text.secondary }} className="text-sm font-medium">
                    Clear all
                  </Text>
                </AnimatedPressable>
              )}
            </View>
          </Animated.View>

          <ScrollView
            className="flex-1"
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={handleRefresh}
                tintColor={colors.primary[600]}
              />
            }
          >
            {notifications.length === 0 ? (
              <Animated.View
                entering={FadeInUp.delay(200).duration(400)}
                className="px-5 py-16 items-center"
              >
                <View
                  className="w-20 h-20 rounded-full items-center justify-center mb-4"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <Bell size={36} color={colors.text.tertiary} />
                </View>
                <Text style={{ color: colors.text.primary }} className="text-lg font-semibold mb-2">
                  All caught up!
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-center">
                  You have no new notifications.{'\n'}We'll let you know when something happens.
                </Text>
              </Animated.View>
            ) : (
              <View className="px-5 py-4 gap-6">
                {/* Today */}
                {groupedNotifications.today.length > 0 && (
                  <Animated.View entering={FadeInUp.delay(100).duration(400)}>
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-sm font-medium mb-3 uppercase tracking-wide"
                    >
                      Today
                    </Text>
                    <View
                      className="rounded-2xl overflow-hidden"
                      style={{
                        backgroundColor: colors.surface.primary,
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.06,
                        shadowRadius: 8,
                        elevation: 3,
                      }}
                    >
                      {groupedNotifications.today.map((notification, index) => (
                        <View key={notification.id}>
                          <NotificationItem
                            notification={notification}
                            onPress={() => handleNotificationPress(notification)}
                            onMarkRead={() => handleMarkRead(notification.id)}
                            onDelete={() => handleDelete(notification.id)}
                            index={index}
                          />
                          {index < groupedNotifications.today.length - 1 && (
                            <View
                              className="mx-4"
                              style={{ height: 1, backgroundColor: colors.border.light }}
                            />
                          )}
                        </View>
                      ))}
                    </View>
                  </Animated.View>
                )}

                {/* Yesterday */}
                {groupedNotifications.yesterday.length > 0 && (
                  <Animated.View entering={FadeInUp.delay(200).duration(400)}>
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-sm font-medium mb-3 uppercase tracking-wide"
                    >
                      Yesterday
                    </Text>
                    <View
                      className="rounded-2xl overflow-hidden"
                      style={{
                        backgroundColor: colors.surface.primary,
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.06,
                        shadowRadius: 8,
                        elevation: 3,
                      }}
                    >
                      {groupedNotifications.yesterday.map((notification, index) => (
                        <View key={notification.id}>
                          <NotificationItem
                            notification={notification}
                            onPress={() => handleNotificationPress(notification)}
                            onMarkRead={() => handleMarkRead(notification.id)}
                            onDelete={() => handleDelete(notification.id)}
                            index={index}
                          />
                          {index < groupedNotifications.yesterday.length - 1 && (
                            <View
                              className="mx-4"
                              style={{ height: 1, backgroundColor: colors.border.light }}
                            />
                          )}
                        </View>
                      ))}
                    </View>
                  </Animated.View>
                )}

                {/* Older */}
                {groupedNotifications.older.length > 0 && (
                  <Animated.View entering={FadeInUp.delay(300).duration(400)}>
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-sm font-medium mb-3 uppercase tracking-wide"
                    >
                      Earlier
                    </Text>
                    <View
                      className="rounded-2xl overflow-hidden"
                      style={{
                        backgroundColor: colors.surface.primary,
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.06,
                        shadowRadius: 8,
                        elevation: 3,
                      }}
                    >
                      {groupedNotifications.older.map((notification, index) => (
                        <View key={notification.id}>
                          <NotificationItem
                            notification={notification}
                            onPress={() => handleNotificationPress(notification)}
                            onMarkRead={() => handleMarkRead(notification.id)}
                            onDelete={() => handleDelete(notification.id)}
                            index={index}
                          />
                          {index < groupedNotifications.older.length - 1 && (
                            <View
                              className="mx-4"
                              style={{ height: 1, backgroundColor: colors.border.light }}
                            />
                          )}
                        </View>
                      ))}
                    </View>
                  </Animated.View>
                )}
              </View>
            )}

            {/* Bottom padding */}
            <View className="h-8" />
          </ScrollView>
        </SafeAreaView>
      </View>
  );

  // Wrap with AppLayout for web
  if (isWeb) {
    return (
      <AppLayout title="Notifications" breadcrumbs={breadcrumbs}>
        {content}
      </AppLayout>
    );
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: 'Notifications',
          headerShown: true,
          headerLeft: () => (
            <Pressable onPress={() => router.back()} className="mr-4">
              <ChevronLeft size={24} color={colors.text.primary} />
            </Pressable>
          ),
        }}
      />
      {content}
    </>
  );
}
